import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ProgressScoreTable as ProgressScoreTableComponent } from "heat-next";

import { useModal } from "@/context/modal/modal";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";
import FlyingCircuitsModalContent from "@/components/ui/modal-with-context/modal-contents/session-details-page/flying-circuits-modal-content/flying-circuits-modal-content";

import base from "@/themes/base/base";

import { useSessionInfo } from "../../hooks/useSessionInfo";

import type { ResultsTableData } from "lib/heat-next/types/types/molecules/results-table.types";
import type {
    CircularProgressBarData,
    Dataset,
} from "lib/heat-next/types/types/organisms/scoring.types";

interface ProgressScoreTableProps {
    disableButton?: boolean;
    onClick?: () => void;
    onStatsClick?: (index: number) => void; //to open Modal in specific stat selected
}

const MODAL_HEIGHT_PERCENTAGE = 0.874;
const CONTENT_HEIGHT_SINGLE_USER = 170;
const CONTENT_HEIGHT_MULTIPLE_USERS = 202;

const ProgressScoreTable: React.FC<ProgressScoreTableProps> = ({
    onStatsClick, //opens corresponding Modal when clicking table cell
}) => {
    const {
        scoreData,
        isFetchingScore,
        isErrorScore,
        subDimensionsData: subDimensionsDataFromContext,
        isFetchingSubDimensions: isFetchingSubDimensionsFromContext,
        circuitFilterOptions,
        setCircuitFilterOptions,
        setSelectedCircuitIndex,
        setSelectedCircuit,
    } = useSessionDetailsContext();

    const { openModal, setWidth, setHeight } = useModal();

    const { getSessionSubtitleDetails, logoUrl, users } = useSessionInfo();

    //to account for single line or multiple line session info
    const MODAL_NON_SCROLLABLE_CONTENT_HEIGHT =
        users?.length <= 1 ? CONTENT_HEIGHT_SINGLE_USER : CONTENT_HEIGHT_MULTIPLE_USERS;

    const [scrollableContainerHeight, setScrollableContainerHeight] = useState(
        () => window.innerHeight * MODAL_HEIGHT_PERCENTAGE - MODAL_NON_SCROLLABLE_CONTENT_HEIGHT,
    );

    const circularProgressBar = scoreData?.datasets?.find((dataset: Dataset) => {
        return dataset.code === "circular-progress-bar";
    });
    const circularProgressBarData = circularProgressBar?.data as CircularProgressBarData;

    const table = scoreData?.datasets?.find((dataset: Dataset) => {
        return dataset.code === "results-table";
    });
    const tableData = (table?.data as ResultsTableData[] | undefined)?.[0];

    const filterOptions = useMemo(() => {
        const resultsTable = scoreData?.datasets?.find((dataset: any) => {
            return dataset.code === "results-table";
        });

        return resultsTable?.data?.[0].answers?.map((item: any) => {
            return item.CorrectAnswer;
        });
    }, [scoreData]);

    useEffect(() => {
        setCircuitFilterOptions(filterOptions);
    }, [scoreData]);

    const isEmptyState =
        !scoreData || scoreData?.datasets?.length === 0 || !circularProgressBarData || !tableData;

    //to update Modals' ComponentsScrollableWrapper height, when screen height changes
    useEffect(() => {
        const handleScrollableContainerResize = () => {
            const updatedHeight =
                window.innerHeight * MODAL_HEIGHT_PERCENTAGE - MODAL_NON_SCROLLABLE_CONTENT_HEIGHT;
            setScrollableContainerHeight(updatedHeight);
        };

        window.addEventListener("resize", handleScrollableContainerResize);

        return () => window.removeEventListener("resize", handleScrollableContainerResize);
    }, []);

    //to update the DOM element's height, since styled components are not receiving the updated height
    useEffect(() => {
        const componentsScrollableWrapper = document.getElementById(
            "components-scrollable-wrapper",
        );

        if (componentsScrollableWrapper) {
            componentsScrollableWrapper.style.height = `${scrollableContainerHeight}px`;
        }
    }, [scrollableContainerHeight]);

    const handleModalOpen = useCallback(
        (selectedIndex = 0) => {
            //for when user clicks ResultsTable
            onStatsClick && onStatsClick(selectedIndex);

            setWidth("77.25682295%"); //for 1104px modal width, in breakpoint 1440px
            setHeight("87.4%");

            setSelectedCircuit(circuitFilterOptions && circuitFilterOptions[selectedIndex]);
            setSelectedCircuitIndex(selectedIndex);

            openModal({
                content: (
                    <FlyingCircuitsModalContent
                        scrollableContainerHeight={scrollableContainerHeight}
                        logoUrl={logoUrl}
                        getSessionSubtitleDetails={getSessionSubtitleDetails}
                        subDimensionsData={subDimensionsDataFromContext}
                    />
                ),
                backgroundColor: `${base.colors.neutral.grey[900]}`,
                borderRadius: "0.25rem",
                padding: "1.5rem 1.5rem 0 1.5rem",
                boxShadow: "0px 0px 4px 1px rgba(255, 255, 255, 0.25)",
            });
        },
        [
            circuitFilterOptions,
            scrollableContainerHeight,
            logoUrl,
            getSessionSubtitleDetails,
            subDimensionsDataFromContext,
            onStatsClick,
        ],
    );

    if (isFetchingScore) {
        return <ContentLoader height="21.3125rem" />;
    }

    if (isEmptyState) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:progress-score-table:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="20.4375rem"
                />
            </ComponentWrapper>
        );
    }

    if (isErrorScore && !isFetchingScore) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="20.4375rem"
                />
            </ComponentWrapper>
        );
    }

    return (
        <ProgressScoreTableComponent
            data={scoreData}
            onClick={() => handleModalOpen()}
            disableButton={isFetchingSubDimensionsFromContext}
            onStatsClick={handleModalOpen}
        />
    );
};

export default ProgressScoreTable;
